import { For, Show, createSignal } from "solid-js";

import { entityDetailLink } from "~/components/entityDetailLink";
import { useAskWorkspace } from "~/lib/hooks";
import type { QaResponse } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

const SUGGESTIONS = [
  "What are my top open deals?",
  "Which leads are hot right now?",
  "Show me accounts in the technology industry",
  "Any contacts at Acme?",
];

/**
 * Natural-language Q&A over workspace data. Retrieval-augmented
 * one-shot (no chat history yet) — the backend pulls relevant accounts
 * + contacts + deals + notes + activities for the question and asks
 * the LLM to answer with citations.
 *
 * Source chips at the bottom link back to each cited entity. Custom
 * records aren't deep-linked yet (would need the parent object key),
 * so they'd render without a link if the agent ever cited one — but
 * the QA agent doesn't query them in v1.x.
 */
export function AskWorkspaceWidget(props: Props) {
  const [question, setQuestion] = createSignal("");
  const [result, setResult] = createSignal<QaResponse | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const askMut = useAskWorkspace(props.wsKey);

  const ask = async (q: string) => {
    if (!q.trim()) return;
    setError(null);
    setResult(null);
    setQuestion(q);
    try {
      const r = await askMut.mutateAsync({ question: q });
      setResult(r);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    void ask(question());
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <h3 class="card-title text-base">💬 Ask the workspace</h3>
        <p class="text-xs opacity-60 mt-0.5">
          Natural-language questions answered from your accounts, contacts, deals, notes, and
          activities.
        </p>

        <form onSubmit={onSubmit} class="flex gap-2 mt-3">
          <input
            type="text"
            class="input input-bordered input-sm flex-1"
            placeholder="Ask anything…"
            value={question()}
            onInput={(e) => setQuestion(e.currentTarget.value)}
            disabled={askMut.isPending}
          />
          <button
            type="submit"
            class="btn btn-primary btn-sm"
            disabled={!question().trim() || askMut.isPending}
          >
            {askMut.isPending ? "Asking…" : "Ask"}
          </button>
        </form>

        <Show when={!result() && !askMut.isPending && !error()}>
          <div class="mt-3 flex flex-wrap gap-1">
            <For each={SUGGESTIONS}>
              {(s) => (
                <button type="button" class="btn btn-ghost btn-xs" onClick={() => void ask(s)}>
                  {s}
                </button>
              )}
            </For>
          </div>
        </Show>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-3">
            {error()}
          </div>
        </Show>

        <Show when={askMut.isPending}>
          <p role="status" aria-live="polite" class="text-sm opacity-60 mt-4">
            Searching workspace + asking the model…
          </p>
        </Show>

        <Show when={result()}>
          {(r) => (
            <div class="mt-4">
              <pre class="whitespace-pre-wrap text-sm font-sans leading-relaxed">{r().answer}</pre>
              <Show when={r().sources.length > 0}>
                <div class="mt-3">
                  <div class="text-xs uppercase opacity-50 mb-1">Sources</div>
                  <div class="flex flex-wrap gap-1">
                    <For each={r().sources}>
                      {(src) => {
                        const link = entityDetailLink(
                          props.wsKey(),
                          src.entity_type,
                          src.entity_id,
                          <span>{src.label}</span>,
                        );
                        return (
                          <Show
                            when={link}
                            fallback={
                              <span class="badge badge-ghost badge-sm">
                                {src.entity_type}: {src.label}
                              </span>
                            }
                          >
                            <span class="badge badge-outline badge-sm">
                              <span class="opacity-60 mr-1">{src.entity_type}:</span>
                              {link}
                            </span>
                          </Show>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </Show>
              <p class="text-xs opacity-50 mt-3">
                <code>{r().model}</code> · {r().input_tokens ?? "?"} in / {r().output_tokens ?? "?"}{" "}
                out tokens
              </p>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}
